import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Upload, MessageSquare, FileText, CheckCircle, ArrowRight, Folder, Search, Clock, Zap, FileSearch, Scale } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import config from '../config';

const HowToUse: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <div className="text-center mb-8 sm:mb-12">
            <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 text-primary-600 mx-auto mb-3 sm:mb-4" />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">How to Use {config.appName}</h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-4">
              A comprehensive guide to automating your document analysis and review
            </p>
          </div>

          {/* Step 1 */}
          <div className="mb-8 sm:mb-12">
            <div className="flex items-start mb-4">
              <div className="bg-primary-100 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                <span className="text-xl sm:text-2xl font-bold text-primary-600">1</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center mb-2 flex-wrap">
                  <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 mr-2 flex-shrink-0" />
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Sign Up & Get Started</h2>
                </div>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Create your free account by clicking the "Sign Up" button on the home page. You'll need to provide:
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-gray-600 ml-4 sm:ml-6">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Your email address</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>A secure password (minimum 8 characters)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Optional: Your display name</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  After registration, you'll be automatically logged in and redirected to the chat interface. 
                  No credit card required—start automating your document work immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-2xl font-bold text-primary-600">2</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <FileText className="h-6 w-6 text-primary-600 mr-2" />
                  <h2 className="text-2xl font-semibold text-gray-900">Upload Your Documents</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Upload your documents to start asking questions. Our platform supports:
                </p>
                <ul className="space-y-2 text-gray-600 ml-6 mb-4">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>PDF files</strong> - Contracts, reports, agreements, policies, etc.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Word documents</strong> - .docx files with business content</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Text files</strong> - Plain text and markdown files</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>URLs</strong> - Web pages and online documents</span>
                  </li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    <strong>Tip:</strong> You can upload multiple documents at once. Organize them by 
                    project, category, or client. The system will process them automatically and extract text, create 
                    searchable chunks, and index them for fast retrieval. Perfect for large document sets with 
                    hundreds of pages.
                  </p>
                </div>
                <p className="text-gray-600">
                  During upload, you'll see real-time progress updates showing the extraction, chunking, and indexing 
                  process. Once complete, your documents are ready for querying. You can start asking questions 
                  immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-2xl font-bold text-primary-600">3</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <MessageSquare className="h-6 w-6 text-primary-600 mr-2" />
                  <h2 className="text-2xl font-semibold text-gray-900">Ask Questions</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Once your documents are uploaded, you can start asking questions in natural language. The AI will:
                </p>
                <ul className="space-y-2 text-gray-600 ml-6 mb-4">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Search through all your uploaded documents</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Find the most relevant information using context</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Provide accurate answers with citations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Show you the source documents and page numbers</span>
                  </li>
                </ul>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 text-sm font-semibold mb-2">
                    Example Questions:
                  </p>
                  <ul className="text-green-800 text-sm space-y-1 ml-4">
                    <li>• "What are the key terms in this contract?"</li>
                    <li>• "Find all mentions of pricing across these documents"</li>
                    <li>• "What are the main findings in this report?"</li>
                    <li>• "Summarize the key points of this agreement"</li>
                    <li>• "What are the compliance requirements in this document?"</li>
                    <li>• "Find all mentions of deadlines and dates"</li>
                    <li>• "What are the key dates and milestones in these documents?"</li>
                  </ul>
                </div>
                <p className="text-gray-600">
                  The AI understands document structure, context, and meaning. Ask questions 
                  naturally—no need to learn complex search syntax. Get instant answers that help you make 
                  informed decisions faster.
                </p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-2xl font-bold text-primary-600">4</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Folder className="h-6 w-6 text-primary-600 mr-2" />
                  <h2 className="text-2xl font-semibold text-gray-900">Organize with Folders</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Organize your documents and conversations using folders. Perfect for managing multiple projects, 
                  clients, or categories. You can:
                </p>
                <ul className="space-y-2 text-gray-600 ml-6">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Create folders for different projects, clients, or categories</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Upload documents directly to specific folders</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Create conversations (threads) within folders</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Keep your workspace organized and easy to navigate</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Share folders with your team for collaboration</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  This organization system helps you manage complex projects with multiple documents, maintain 
                  confidentiality by separating categories, and build a searchable knowledge base of your 
                  work.
                </p>
              </div>
            </div>
          </div>

          {/* Best Practices */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Be specific:</strong> More specific questions yield better results. Instead of "What's in this document?" ask "What are the key terms and pricing details?"</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Use relevant terminology:</strong> The AI understands business and industry terms, so use them naturally. Ask about specific concepts relevant to your documents.</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Check citations:</strong> Always review source documents for accuracy. Click through to verify every answer and make decisions with confidence.</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Organize early:</strong> Set up folders before uploading many documents. This makes it easier to manage projects and find information later.</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Ask follow-up questions:</strong> Build on previous answers. The AI remembers context within a conversation thread, making complex analysis easier.</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Use for analysis:</strong> Upload reports, contracts, and business documents. Ask questions to find patterns, key information, and insights.</span>
              </li>
            </ul>
          </div>

          {/* Use Cases Quick Reference */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Common Use Cases</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <Scale className="h-5 w-5 text-primary-600 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Contract Review</h4>
                  <p className="text-sm text-gray-600">Identify key terms, risks, and unusual clauses quickly</p>
                </div>
              </div>
              <div className="flex items-start">
                <FileSearch className="h-5 w-5 text-primary-600 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Document Analysis</h4>
                  <p className="text-sm text-gray-600">Extract key information, insights, and patterns from documents</p>
                </div>
              </div>
              <div className="flex items-start">
                <Search className="h-5 w-5 text-primary-600 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Intelligent Search</h4>
                  <p className="text-sm text-gray-600">Find relevant information across your document library</p>
                </div>
              </div>
              <div className="flex items-start">
                <FileText className="h-5 w-5 text-primary-600 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Due Diligence</h4>
                  <p className="text-sm text-gray-600">Review hundreds of documents for transactions</p>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/use-cases"
                className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center text-sm"
              >
                View All Use Cases <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-semibold mb-4">Ready to Automate Your Document Work?</h3>
            <p className="text-primary-100 mb-6">
              Start using our app today and see how much time you can save on document review and analysis.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/register"
                className="bg-white text-primary-600 px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors font-semibold inline-flex items-center justify-center"
              >
                Sign Up Free
              </Link>
              <Link
                to="/use-cases"
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition-colors font-semibold inline-flex items-center justify-center"
              >
                View Use Cases
              </Link>
            </div>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default HowToUse;
